// Appels serveur du module « Organisation » (functions/api/modules/organisation.js) — maquette L01.
// Le super-admin passe `clientId` : le serveur lit alors le compte de ce client (`?client_id=`).
import { apiFetch } from './api';

export type TypeService = 'hotel' | 'restaurant' | 'spa' | 'prestations' | 'evenementiel' | 'autre';
export type NiveauCloisonnement = 'tout_voir' | 'planning_equipe' | 'nom_seul';
export type EtatModule = 'actif' | 'inactif' | 'verrouille';

export const LIBELLES_TYPE_SERVICE: Record<TypeService, string> = {
    hotel: 'Hôtel', restaurant: 'Restaurant', spa: 'Spa', prestations: 'Prestations', evenementiel: 'Événementiel', autre: 'Autre',
};

export const NIVEAUX_CLOISONNEMENT: { value: NiveauCloisonnement; label: string }[] = [
    { value: 'tout_voir', label: 'Tout voir' },
    { value: 'planning_equipe', label: 'Planning et équipe seulement' },
    { value: 'nom_seul', label: 'Nom du service seulement' },
];

export interface ServiceContexte {
    id: string; type: TypeService; type_libelle: string; name: string; modules_actifs: number; modules: string[];
}
export interface ContexteOrganisation {
    role: 'admin' | 'direction' | 'collaborateur';
    organisation: { id: string; name: string };
    etablissement: { id: string; name: string; client_id: string };
    services: ServiceContexte[];
    modules_transverses: string[];
    selecteur_visible: boolean;
}

export interface ServiceFiche {
    id: string; client_id: string; etablissement_id: string; type: TypeService; type_libelle: string; name: string;
    societe: string; siret: string; numero_tva: string; paiement_statut: 'en_attente' | 'connecte'; autre_societe: boolean;
    cloisonnement: NiveauCloisonnement; responsable_nom: string; ordre: number; modules_actifs: number;
}
export interface EtablissementArbre {
    id: string; client_id: string; name: string; ville: string; adresse: string; personnes: number; services: ServiceFiche[];
}
export interface ArbreOrganisation {
    organisation: { id: string; name: string; modules_transverses_actifs: number };
    etablissements: EtablissementArbre[];
}

export interface PlageHoraire { de: string; a: string }
export interface JourHoraires { jour: number; ouvert: boolean; plages: PlageHoraire[] }
export interface FicheEtablissement {
    id: string; client_id: string; organisation_id: string; name: string; adresse: string; code_postal: string; ville: string;
    siret: string; logo_url: string | null; horaires: JourHoraires[]; devise: string; fuseau: string; langue: string;
}

export interface ModuleCatalogue {
    name: string; label: string; famille: string; description: string; statut: 'disponible' | 'bientot';
    transverse: boolean; core: boolean; etat: EtatModule; raison: string | null; a_terminer: boolean; activated_at: string | null;
    prerequis: { type: 'module'; name: string; label: string; ok: boolean; bientot?: boolean }[];
    bon_avec?: { name: string; label: string }[];
    /** Demande d'activation du client : en attente de réponse, ou refusée (avec la réponse de L'IAmani). */
    demande: 'en_attente' | 'refusee' | null; reponse_demande: string;
    /** Tarif actuel du module (le prix est figé quand le client l'obtient). */
    tarif?: { mode: 'gratuit' | 'fixe' | 'paliers'; prix_eur: number; paliers: { quantite: number; prix_eur: number }[] };
}
export interface DemandeModule {
    id: string; client_id: string; etablissement: string; service_id: string; service: string;
    module_name: string; module_label: string; message: string; demandee_par: string; created_at: string;
}
export interface MetriqueUsage {
    metrique: string; libelle: string; limite: number | null; consommation: number; pourcent: number | null;
    niveau: 'aucune_limite' | 'ok' | 'seuil' | 'atteinte';
}
export interface LigneUsage {
    service_id: string; service_name: string; module_name: string; label: string; activated_at: string | null; metriques: MetriqueUsage[];
}
export interface DroitsUsage { mois: string; lignes: LigneUsage[]; alerte: (MetriqueUsage & { module: string }) | null }

const q = (clientId?: string) => (clientId ? `?client_id=${encodeURIComponent(clientId)}` : '');
const json = (b: unknown) => JSON.stringify(b);

export const organisationApi = {
    contexte: (clientId?: string): Promise<ContexteOrganisation> => apiFetch(`/organisation/contexte${q(clientId)}`),
    arbre: (clientId?: string): Promise<ArbreOrganisation> => apiFetch(`/organisation/arbre${q(clientId)}`),
    etablissement: (clientId?: string): Promise<FicheEtablissement> => apiFetch(`/organisation/etablissement${q(clientId)}`),
    enregistrerEtablissement: (payload: Partial<FicheEtablissement>, clientId?: string): Promise<FicheEtablissement> =>
        apiFetch(`/organisation/etablissement${q(clientId)}`, { method: 'PUT', body: json(payload) }),
    service: (id: string, clientId?: string): Promise<ServiceFiche> => apiFetch(`/organisation/services/${id}${q(clientId)}`),
    creerService: (payload: Partial<ServiceFiche>, clientId?: string): Promise<ServiceFiche> =>
        apiFetch(`/organisation/services${q(clientId)}`, { method: 'POST', body: json(payload) }),
    enregistrerService: (id: string, payload: Partial<ServiceFiche>, clientId?: string): Promise<ServiceFiche> =>
        apiFetch(`/organisation/services/${id}${q(clientId)}`, { method: 'PUT', body: json(payload) }),
    retirerService: (id: string, clientId?: string) => apiFetch(`/organisation/services/${id}${q(clientId)}`, { method: 'DELETE' }),
    modules: (serviceId: string, clientId?: string): Promise<{ service: ServiceFiche; modules: ModuleCatalogue[] }> =>
        apiFetch(`/organisation/services/${serviceId}/modules${q(clientId)}`),
    basculerModule: (serviceId: string, nom: string, actif: boolean, clientId?: string): Promise<{ module: ModuleCatalogue; message: string | null }> =>
        apiFetch(`/organisation/services/${serviceId}/modules/${nom}${q(clientId)}`, { method: 'PUT', body: json({ actif }) }),
    demanderModule: (serviceId: string, nom: string, message: string, confirmePrix = false): Promise<{ success: boolean }> =>
        apiFetch(`/organisation/services/${serviceId}/modules/${nom}/demande`, { method: 'POST', body: json({ message, confirme_prix: confirmePrix }) }),
    /** Paie par carte le premier mois du module : renvoie l'adresse de la page de paiement Stripe. Le module s'active au paiement confirmé. */
    payerModule: (serviceId: string, nom: string): Promise<{ url: string; montant_eur: number }> =>
        apiFetch(`/organisation/services/${serviceId}/modules/${nom}/payer`, { method: 'POST', body: json({ confirme_prix: true }) }),
    droitsUsage: (clientId?: string): Promise<DroitsUsage> => apiFetch(`/organisation/droits-usage${q(clientId)}`),
    fixerLimite: (clientId: string, payload: { service_id: string; module_name: string; metrique: string; limite: number | null }) =>
        apiFetch(`/organisation/droits-usage${q(clientId)}`, { method: 'PUT', body: json(payload) }),
};

/** Message lisible d'une erreur d'API (`{ status, message }`). */
export const messageErreur = (e: unknown, repli = 'Une erreur est survenue.'): string =>
    (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') ? (e as { message: string }).message : repli;

export const statutErreur = (e: unknown): number | null =>
    (e && typeof e === 'object' && 'status' in e && typeof (e as { status: unknown }).status === 'number') ? (e as { status: number }).status : null;

// --- Groupes d'établissements (super-admin) ---
export interface EtablissementDeGroupe { client_id: string; name: string; ville: string; services: number }
export interface GroupeEtablissements { id: string; name: string; etablissements: EtablissementDeGroupe[] }
export interface EtablissementSeul extends EtablissementDeGroupe { organisation_id: string }

export const groupesApi = {
    lister: (): Promise<{ groupes: GroupeEtablissements[]; seuls: EtablissementSeul[] }> => apiFetch('/organisation/groupes'),
    rattacher: (payload: { client_id: string; organisation_id?: string; nouveau_nom?: string }) =>
        apiFetch('/organisation/groupes/rattacher', { method: 'POST', body: json(payload) }),
    detacher: (client_id: string) => apiFetch('/organisation/groupes/detacher', { method: 'POST', body: json({ client_id }) }),
    renommer: (id: string, name: string) => apiFetch(`/organisation/groupes/${id}`, { method: 'PUT', body: json({ name }) }),
};

// --- Demandes d'activation de module (super-admin) ---
export const demandesModulesApi = {
    lister: (): Promise<{ demandes: DemandeModule[] }> => apiFetch('/organisation/demandes'),
    accepter: (id: string) => apiFetch(`/organisation/demandes/${id}/accepter`, { method: 'POST', body: '{}' }),
    refuser: (id: string, reponse: string) => apiFetch(`/organisation/demandes/${id}/refuser`, { method: 'POST', body: json({ reponse }) }),
};

// --- Pôles et groupe (lots L7 et L8) ---
export type GenreDemandePole = 'pole' | 'groupe' | 'rejoindre';
export type StatutDemandePole = 'en_attente' | 'accordee' | 'refusee';
export interface DemandePole {
    id: string; client_id: string; etablissement: string; genre: GenreDemandePole; cas: '' | 'service' | 'etablissement'; type_pole: string; type_pole_libelle: string;
    nom: string; siret: string; precisions: string; code: string; statut: StatutDemandePole; motif: string; resultat: string; demandee_par: string; created_at: string; traite_le: string | null;
}
export interface TarifsGroupe { pole_prix_eur: number; remises: { seuil: number; pct: number }[] }
export interface PolesGroupeClient {
    tarifs: TarifsGroupe;
    groupe: { organisation_id: string | null; nom: string; est_groupe: boolean; etablissements: { client_id: string; name: string; siret: string }[]; nombre: number; remise_pct: number };
    poles: { id: string; name: string; pole_type: string; siret: string }[];
    invitations: { code: string; nom_invite: string; siret_invite: string; statut: string; created_at: string }[];
    demandes: DemandePole[];
    types_pole: { value: string; label: string }[];
}
export interface NouvelleDemandePole { genre: 'pole' | 'groupe'; cas?: 'service' | 'etablissement'; type_pole?: string; nom: string; siret?: string; precisions?: string }

export const polesGroupeApi = {
    voir: (clientId?: string): Promise<PolesGroupeClient> => apiFetch(`/organisation/poles-groupe${q(clientId)}`),
    demander: (d: NouvelleDemandePole): Promise<{ success: boolean; id: string }> => apiFetch('/organisation/poles-groupe/demandes', { method: 'POST', body: json(d) }),
    annuler: (id: string) => apiFetch(`/organisation/poles-groupe/demandes/${id}`, { method: 'DELETE' }),
    inviter: (nom: string): Promise<{ success: boolean; code: string }> => apiFetch('/organisation/poles-groupe/invitations', { method: 'POST', body: json({ nom }) }),
    annulerInvitation: (code: string) => apiFetch(`/organisation/poles-groupe/invitations/${encodeURIComponent(code)}`, { method: 'DELETE' }),
    rejoindre: (code: string) => apiFetch('/organisation/poles-groupe/rejoindre', { method: 'POST', body: json({ code }) }),
    // Super-admin
    admin: (): Promise<{ en_attente: DemandePole[]; traitees: DemandePole[]; tarifs: TarifsGroupe }> => apiFetch('/organisation/poles-groupe/admin'),
    accorder: (id: string, motif: string): Promise<{ success: boolean; resultat: string }> => apiFetch(`/organisation/poles-groupe/demandes/${id}/accepter`, { method: 'POST', body: json({ motif }) }),
    refuser: (id: string, motif: string) => apiFetch(`/organisation/poles-groupe/demandes/${id}/refuser`, { method: 'POST', body: json({ motif }) }),
    enregistrerTarifs: (t: TarifsGroupe): Promise<{ success: boolean; tarifs: TarifsGroupe }> => apiFetch('/organisation/poles-groupe/tarifs', { method: 'PUT', body: json(t) }),
};
